import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Share2, Search, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { trackEvent } from '@/lib/trackEvent';

interface PostReviewCTAProps {
  symbol: string;
  verdict?: string;
  onNewSearch: () => void;
}

export function PostReviewCTA({ symbol, verdict, onNewSearch }: PostReviewCTAProps) {
  const { user } = useAuth();

  const shareUrl = `${window.location.origin}/coin-reviewer?coin=${encodeURIComponent(symbol.toLowerCase())}`;
  const shareTitle = `${symbol.toUpperCase()} AI review${verdict ? ` — ${verdict}` : ''} | GoToCoin`;

  const handleShare = async () => {
    trackEvent('coin_review_shared', { coin: symbol, method: typeof navigator !== 'undefined' && navigator.share ? 'native' : 'clipboard' });
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareTitle, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Review link copied to clipboard');
    } catch {
      /* user dismissed the share sheet — nothing to do */
    }
  };

  const handleSave = () => {
    trackEvent('interaction', { type: 'coin_review_save_attempt', coin: symbol, signed_in: !!user });
    if (!user) {
      toast.info('Sign in to save reviews to your dashboard');
      return;
    }
    toast.success('Saved — your dashboard is coming soon');
  };

  return (
    <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-purple-500/10 to-pink-500/10">
      <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <h3 className="text-lg font-bold">Happy with this {symbol.toUpperCase()} review?</h3>
          <p className="text-sm text-muted-foreground">Share it with your mates or run another coin — no signup needed.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleShare} size="lg" className="gap-2 bg-gradient-to-r from-primary to-purple-500">
            <Share2 className="h-4 w-4" />
            Share your review
          </Button>
          <Button onClick={onNewSearch} size="lg" variant="outline" className="gap-2">
            <Search className="h-4 w-4" />
            Review another coin
          </Button>
          {user && (
            <Button onClick={handleSave} size="lg" variant="ghost" className="gap-2">
              <Bookmark className="h-4 w-4" />
              Save to dashboard
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
